import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SearchIcon from '@mui/icons-material/Search';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VerifiedIcon from '@mui/icons-material/Verified';
import RecommendIcon from '@mui/icons-material/Recommend';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InsightsIcon from '@mui/icons-material/Insights';
import { useAuth } from '../../contexts/AuthContext';
import type { FeatureFlags } from '@traaker/shared';

type NavItem = {
  path: string;
  label: string;
  icon: React.ReactNode;
  requireFeature?: keyof FeatureFlags;
};
type NavSection = { title: string; items: NavItem[] };

const OVERVIEW_ITEM: NavItem = { path: '/', label: 'Dashboard', icon: <DashboardIcon /> };

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Define',
    items: [
      { path: '/strategy', label: 'Brand Knowledge', icon: <AccountTreeIcon /> },
      { path: '/geo-strategy', label: 'GEO Strategy', icon: <AutoAwesomeIcon /> },
    ],
  },
  {
    title: 'Audit',
    items: [
      { path: '/audits', label: 'Audit Website', icon: <SearchIcon /> },
      { path: '/earned-media', label: 'Earned Media', icon: <NewspaperIcon /> },
      { path: '/competitors', label: 'Concurrents', icon: <CompareArrowsIcon />, requireFeature: 'competitorsEnabled' },
    ],
  },
  {
    title: 'Act',
    items: [
      { path: '/tasks', label: 'Tâches', icon: <TaskAltIcon />, requireFeature: 'tasksEnabled' },
      { path: '/news-watch', label: 'Veille news', icon: <EditNoteIcon />, requireFeature: 'newsWatchEnabled' },
    ],
  },
  {
    title: 'Measure',
    items: [
      { path: '/brand/narrative', label: 'Brand Narrative', icon: <MenuBookIcon /> },
      { path: '/brand/authority', label: 'Brand Authority', icon: <VerifiedIcon /> },
      { path: '/brand/referral', label: 'Brand Referral', icon: <RecommendIcon /> },
    ],
  },
  {
    title: 'Report',
    items: [
      { path: '/reports/audit', label: "Rapport d'audit", icon: <AssessmentIcon />, requireFeature: 'reportsEnabled' },
      { path: '/reports/status', label: "Rapport d'activité", icon: <InsightsIcon />, requireFeature: 'reportsEnabled' },
    ],
  },
];

interface SidebarProps {
  width: number;
}

export default function Sidebar({ width }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, tenant } = useAuth();
  const flags = tenant?.settings?.featureFlags;
  const itemVisible = (item: NavItem) => !item.requireFeature || flags?.[item.requireFeature] === true;

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const renderItem = (item: NavItem) => {
    const active = isActive(item.path);
    return (
      <ListItemButton
        key={item.path}
        onClick={() => navigate(item.path)}
        selected={active}
        sx={{
          borderRadius: 2,
          mb: 0.5,
          '&.Mui-selected': {
            bgcolor: 'rgba(124, 58, 237, 0.10)',
            '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.16)' },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 40, color: active ? 'primary.main' : 'text.secondary' }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize: '0.875rem',
            fontWeight: active ? 600 : 500,
          }}
        />
      </ListItemButton>
    );
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          component="img"
          src="/traaker-icon.svg"
          alt=""
          sx={{ height: 32, width: 32, objectFit: 'contain', display: 'block' }}
        />
        <Typography
          sx={{
            fontFamily: '"Inter Tight", "Inter", sans-serif',
            fontWeight: 700,
            fontSize: '1.15rem',
            letterSpacing: '0.02em',
            lineHeight: 1,
            textTransform: 'uppercase',
            color: 'text.primary',
          }}
        >
          TR<Box component="span" sx={{ color: 'primary.main' }}>A</Box>AKER
        </Typography>
      </Box>

      <Divider />

      <List sx={{ px: 1, mt: 1 }}>{renderItem(OVERVIEW_ITEM)}</List>

      {NAV_SECTIONS.map((section) => {
        const visibleItems = section.items.filter(itemVisible);
        if (visibleItems.length === 0) return null;
        return (
          <Box key={section.title} sx={{ mt: 1.5 }}>
            <Typography
              variant="overline"
              sx={{
                px: 2.5,
                color: 'text.secondary',
                fontSize: '0.6875rem',
                fontWeight: 600,
                letterSpacing: '0.08em',
              }}
            >
              {section.title}
            </Typography>
            <List sx={{ px: 1, pt: 0.5 }}>{visibleItems.map(renderItem)}</List>
          </Box>
        );
      })}

      <Box sx={{ flexGrow: 1 }} />

      <List sx={{ px: 1, pb: 2 }}>
        {user?.isPlatformAdmin && (
          <ListItemButton
            onClick={() => navigate('/admin/tenants')}
            selected={location.pathname.startsWith('/admin')}
            sx={{ borderRadius: 2, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <AdminPanelSettingsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Admin"
              primaryTypographyProps={{ fontSize: '0.875rem' }}
            />
          </ListItemButton>
        )}
        <ListItemButton
          onClick={() => navigate('/settings')}
          selected={location.pathname === '/settings'}
          sx={{ borderRadius: 2 }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText
            primary="Paramètres"
            primaryTypographyProps={{ fontSize: '0.875rem' }}
          />
        </ListItemButton>
      </List>
    </Drawer>
  );
}
